import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Variationer: holder den valgte størrelse, når kunden hopper til en anden udgave
 * af produktet, fx en anden farve.
 *
 * Udgaverne er selvstændige produkter, så hvert link peger på et andet produkt.
 * Liquid renderer linkene med den størrelse, der er valgt, når siden loades.
 * Skifter kunden derefter størrelse, skriver denne komponent ?variant= om, så
 * linket rammer den samme størrelse i den nye udgave.
 *
 * Hvert link har et data-variants med { nøgle: variant-id } for sit eget produkt.
 * Nøglen bygges af snippets/locktown-variant-key.liquid. Ændres formatet der,
 * skal buildKey nedenfor ændres med.
 */

/**
 * Nøglen for et sæt valgte optioner, fx "størrelse=xs".
 * @param {{ name: string, value: string }[]} selectedOptions
 * @returns {string}
 */
function buildKey(selectedOptions) {
  return selectedOptions
    .map(({ name, value }) => `${String(name).trim().toLowerCase()}=${String(value).trim().toLowerCase()}`)
    .filter((pair) => !pair.startsWith('=') && !pair.endsWith('='))
    .sort()
    .join('~');
}

/**
 * @typedef {Object} LocktownVariationsRefs
 * @property {HTMLAnchorElement[]} [links]
 */

/** @extends {Component<LocktownVariationsRefs>} */
class LocktownVariations extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Lyt på sektionen, ikke på document. I quick-add er det dialogen.
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  get #scope() {
    return this.closest('.shopify-section, dialog');
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Swatches på produktkort i samme sektion skal ikke flytte rundt på linkene her.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Eventets id er et gid, fx "gid://shopify/Product/123". Liquid giver os tallet.
    const eventProductId = String(event.product?.id ?? '').split('/').pop();
    if (eventProductId && this.dataset.productId && eventProductId !== this.dataset.productId) return;

    const { selectedOptions } = event;
    if (!Array.isArray(selectedOptions) || selectedOptions.length === 0) return;

    const key = buildKey(selectedOptions);

    for (const link of this.refs.links ?? []) {
      const url = link.dataset.url;
      if (!url) continue;

      let variants;
      try {
        variants = JSON.parse(link.dataset.variants || '{}');
      } catch {
        continue;
      }

      const variantId = variants[key];
      // Har udgaven ikke den valgte størrelse, sendes kunden til produktet uden ?variant=.
      link.href = variantId ? `${url}?variant=${variantId}` : url;
    }
  };
}

if (!customElements.get('locktown-variations-component')) {
  customElements.define('locktown-variations-component', LocktownVariations);
}
